import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { STOP_IMPERSONATING, useImpersonation, useSubmitImpersonationMapping } from '../../hooks/useImpersonation';
import { isDelphiRoute } from '../../hooks/useGlobalMasterSearchShortcut';
import { openImpersonationPalette } from '../../lib/impersonation-palette-store';
import { useImpersonateWriteModeStore } from '../../lib/impersonate-write-mode';

/**
 * Impersonation indicator pill.
 *
 * Fixed-position floating pill at bottom-center of the viewport.
 * Shows "Viewing as email" + controls when impersonating another user.
 *
 * Reads all impersonation state from `useImpersonation()` — the URL param
 * is the single source of frontend truth (synced from delegation if needed).
 *
 * The Unlock writes control renders in every environment because the lock is identical everywhere.
 * See `src/auth/write-guard.ts` for the rule (`ctx.writesUnlocked`).
 *
 * Stands down on `/tools/delphi/**`, like the global palettes: Delphi renders
 * its own pill there (`mini-apps/delphi/components/ImpersonationPill.tsx`).
 */
export default function ImpersonationBanner() {
  const { active, actingAsName, actingAsId, canUnlockWrites, target } = useImpersonation();
  const isDemo = target === 'demo';
  const submitMapping = useSubmitImpersonationMapping();
  const { pathname } = useLocation();
  const [isReverting, setIsReverting] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const writesEnabled = useImpersonateWriteModeStore((s) => s.enabled);


  if (!active || isDelphiRoute(pathname)) return null;

  const displayName = isDemo ? null : actingAsName || actingAsId;

  const handleStop = async () => {
    setIsReverting(true);
    setShowConfirmation(false);
    try {
      await submitMapping(STOP_IMPERSONATING);
      // Full page reload resets all client state (React Query caches, SSE
      // streams, user-controls) for the restored identity, while staying on
      // the current route — a router navigate would leave stale
      // identity-scoped state behind.
      window.location.reload();
    } catch {
      toast.error('Failed to stop impersonation');
    } finally {
      setIsReverting(false);
    }
  };

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] flex flex-col items-center gap-2">
      {showConfirmation && (
        <div className="rounded-lg bg-white dark:bg-nightshade-900 px-4 py-3 shadow-lg border border-primary dark:border-nightshade-700 max-w-sm text-xs text-content-secondary dark:text-nightshade-400">
          <p>
            You're about to enable writes as <span className="font-semibold text-content-primary dark:text-nightshade-100">{displayName}</span>.
            Actions you execute will affect their data and be logged with your name. Continue?
          </p>
          <div className="mt-2 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setShowConfirmation(false)}
              className="rounded px-3 py-1 text-xs font-medium text-content-secondary dark:text-nightshade-400 hover:bg-bg-secondary dark:hover:bg-nightshade-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                useImpersonateWriteModeStore.getState().enable();
                setShowConfirmation(false);
              }}
              className="rounded bg-pomegranate-600 px-3 py-1 text-xs font-semibold text-white hover:bg-pomegranate-700 transition-colors"
            >
              Enable writes
            </button>
          </div>
        </div>
      )}
      <div
        className={`flex items-center gap-3 rounded-full px-4 py-2 text-white shadow-lg ${
          writesEnabled ? 'bg-pomegranate-600' : 'bg-tangerine-500'
        }`}
      >
        <span className="text-xs font-medium whitespace-nowrap">
          {isDemo ? (
            <span className="font-bold tracking-wide">DEMO</span>
          ) : writesEnabled ? (
            <>
              <span className="font-bold tracking-wide">WRITES ENABLED</span>
              {' · '}{displayName}
            </>
          ) : (
            <>
              Viewing as <span className="font-semibold">{displayName}</span>
              <span className="ml-1.5 opacity-75">· Read-only</span>
            </>
          )}
        </span>
        {writesEnabled ? (
          <button
            type="button"
            onClick={() => useImpersonateWriteModeStore.getState().disable()}
            className="rounded-full bg-white/20 px-3 py-0.5 text-xs font-semibold text-white hover:bg-white/30 transition-colors"
          >
            Lock
          </button>
        ) : canUnlockWrites ? (
          <button
            type="button"
            onClick={() => setShowConfirmation(true)}
            className="rounded-full bg-pomegranate-600 px-3 py-0.5 text-xs font-semibold text-white hover:bg-pomegranate-700 transition-colors"
          >
            Unlock writes
          </button>
        ) : null}
        {!isDemo && (
          <button
            type="button"
            onClick={openImpersonationPalette}
            className="rounded-full bg-white/20 px-3 py-0.5 text-xs font-semibold text-white hover:bg-white/30 transition-colors"
          >
            Change
          </button>
        )}
        <button
          type="button"
          onClick={() => { void handleStop(); }}
          disabled={isReverting}
          className="rounded-full bg-white/20 px-3 py-0.5 text-xs font-semibold text-white hover:bg-white/30 disabled:opacity-50 transition-colors"
        >
          {isReverting ? 'Stopping\u2026' : 'Stop'}
        </button>
      </div>
    </div>
  );
}
